import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Card,
    Col,
    Empty,
    Input,
    Modal,
    Pagination,
    Row,
    Space,
    Tag,
    Spin,
} from 'antd';
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';

import { getIndicators } from '../../core/indicator/indicatorApi';
import { AppDispatch, CombinedStates } from '../../core/types';
import { Indicator, IndicatorTargetValue } from '../../core/indicator/types';
import { frequencyOptions as baseFrequencyOptions } from '../../core/utils/helpers';
import './missingIndicatorsButton.scss';

type ExtraIndicatorFields = {
    source_document?: string | null;
    data_source?: string | null;
    target_value?: string | null;
    target_values?: IndicatorTargetValue[];
    formula?: string | null;
    unit_description?: string | null;
};

const MissingIndicatorsButton = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { indicators, isFetching } = useSelector((state: CombinedStates) => state.indicator);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (isModalOpen && indicators.length === 0) {
            dispatch(getIndicators({}));
        }
    }, [dispatch, indicators.length, isModalOpen]);

    const filteredIndicators = (() => {
        let filtered = indicators;

        // If user is a school (role_id === 5), always filter out education_center and education_department scopes
        if (userData?.role_id === 5) {
            filtered = filtered.filter((indicator: Indicator) => (
                indicator.assigned_scope !== 'education_center'
                && indicator.assigned_scope !== 'education_department'
            ));
        }

        // If user is a school (role_id === 5), additionally filter to show only active indicators (status === 10)
        if (userData?.role_id === 5) {
            filtered = filtered.filter((indicator: Indicator) => {
                const indicatorStatus = indicator.status ?? 10;
                return indicatorStatus === 10;
            });
        }

        const term = debouncedSearch.trim().toLowerCase();
        if (!term) return filtered;

        const matchField = (value?: string | null) => (value ? value.toLowerCase().includes(term) : false);
        const numericPart = term.startsWith('r') ? term.slice(1) : term;
        const isNumericSearch = /^\d+$/.test(numericPart);

        return filtered.filter((indicator: Indicator) => {
            const { source_document, data_source, target_value, formula } = indicator as Indicator & ExtraIndicatorFields;
            const titleMatch = indicator.title.toLowerCase().includes(term);
            const descriptionMatch = matchField(indicator.description);
            const idMatch = isNumericSearch
                ? indicator.id.toString().includes(numericPart)
                : indicator.id.toString().includes(term);
            const sourceDocMatch = matchField(source_document);
            const dataSourceMatch = matchField(data_source);
            const targetValueMatch = matchField(target_value);
            const formulaMatch = matchField(formula);

            return titleMatch
                || descriptionMatch
                || idMatch
                || sourceDocMatch
                || dataSourceMatch
                || targetValueMatch
                || formulaMatch;
        });
    })();

    const total = filteredIndicators.length;
    const paginatedIndicators = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredIndicators.slice(start, start + pageSize);
    }, [filteredIndicators, page, pageSize]);

    type ScopeKey = 'school' | 'education_center' | 'education_department';
    const defaultScope: ScopeKey = 'school';
    const scopeKeys: ScopeKey[] = ['school', 'education_center', 'education_department'];

    const scopeStyles: Record<ScopeKey, { bg: string; border: string }> = {
        school: { bg: '#ffffff', border: '#e5e7eb' },
        education_center: { bg: '#f0f7ff', border: '#cfe3ff' },
        education_department: { bg: '#f6ffed', border: '#d9f7be' },
    };
    const scopeLabels: Record<ScopeKey, string> = {
        school: 'Mokyklos rodiklis',
        education_center: 'Švietimo centras',
        education_department: 'Švietimo skyrius',
    };

    const isScopeKey = (scope?: string): scope is ScopeKey => scopeKeys.includes((scope || '') as ScopeKey);

    const getScopeStyle = (scope?: string) => {
        const key: ScopeKey = isScopeKey(scope) ? scope : defaultScope;
        return scopeStyles[key];
    };

    const getScopeLabel = (scope?: string) => {
        const key: ScopeKey = isScopeKey(scope) ? scope : defaultScope;
        return scopeLabels[key];
    };

    const getFrequencyLabel = (freq: string) => baseFrequencyOptions[freq]?.label || freq;
    const getFrequencyColor = (freq: string) => baseFrequencyOptions[freq]?.color;

    const getStatusMeta = (status?: number) => {
        const normalized = status ?? 10;
        if (normalized === 10) return { label: 'Aktyvus', color: 'green' };
        if (normalized === 0) return { label: 'Neaktyvus', color: 'red' };
        return { label: `Statusas ${normalized}`, color: 'default' };
    };

    const renderMetaRow = (label: string, value?: React.ReactNode) => {
        if (!value) return null;
        return (
            <div className="meta-row">
                <span className="meta-label">{label}</span>
                <span className="meta-value">{value}</span>
            </div>
        );
    };

    const renderSectionTitle = (text: string) => (
        <div className="section-title">{text}</div>
    );

    const renderChip = (label: string, value?: React.ReactNode) => {
        if (!value) return null;
        return (
            <Tag className="info-chip">
                <span className="chip-label">{label}:</span> <span className="chip-value">{value}</span>
            </Tag>
        );
    };

    const renderBadges = (indicator: Indicator & ExtraIndicatorFields) => (
        <Space size={6} wrap className="badge-row">
            {!!indicator.is_critical && <Tag color="red">Kritinis</Tag>}
            {!!indicator.is_computed && <Tag color="purple">Skaičiuojamas</Tag>}
        </Space>
    );

    const renderIndicatorCard = (indicator: Indicator) => {
        const style = getScopeStyle(indicator.assigned_scope);
        const scopeLabel = getScopeLabel(indicator.assigned_scope);
        const extended = indicator as Indicator & ExtraIndicatorFields;
        const statusMeta = getStatusMeta(indicator.status);

        return (
            <Col span={12} key={indicator.id} xs={24} sm={24} md={12} lg={12} xl={12}>
                <Card
                    className="missing-indicator-card"
                    style={{ backgroundColor: style.bg, borderColor: style.border }}
                    hoverable>
                    <div className="card-header">
                        <div className="title-block">
                            <h4>{indicator.title}</h4>
                            <Tag color="geekblue">R{indicator.id}</Tag>
                        </div>
                        <Tag className="scope-pill" color={style.border} style={{ color: '#1f2a44' }}>
                            {scopeLabel}
                        </Tag>
                    </div>

                    {indicator.categories && indicator.categories.length > 0 && (
                        <div className="section">
                            {renderSectionTitle('Kategorijos')}
                            <Space size={[8, 8]} wrap className="category-tags">
                                {indicator.categories.map((cat) => (
                                    <Tag key={cat.id} color="blue">
                                        {cat?.full_path || cat.title}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    )}

                    {indicator.description && (
                        <div className="section">
                            {renderSectionTitle('Aprašymas')}
                            <p className="indicator-description">{indicator.description}</p>
                        </div>
                    )}

                    <div className="section">
                        {renderSectionTitle('Trumpa santrauka')}
                        <Space size={[8, 8]} wrap className="summary-chips">
                            {renderChip('Kam taikomas', scopeLabel)}
                            {getFrequencyLabel(indicator.input_frequency) && (
                                <Tag color={getFrequencyColor(indicator.input_frequency) || 'default'}>
                                    <span className="chip-label">Dažnumas:</span> <span className="chip-value">{getFrequencyLabel(indicator.input_frequency)}</span>
                                </Tag>
                            )}
                            {renderChip('Vienetas', indicator.unit)}
                            {renderChip('ID', `R${indicator.id}`)}
                        </Space>
                    </div>

                    <div className="section">
                        {renderSectionTitle('Žymos')}
                        <Space size={[8, 8]} wrap>
                            <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                            {renderBadges(extended)}
                        </Space>
                    </div>

                    <div className="section">
                        {renderSectionTitle('Papildoma informacija')}
                        {((extended.source_document && extended.source_document.trim())
                            || (extended.data_source && extended.data_source.trim())
                            || (extended.target_value && extended.target_value.trim())) && (
                            <div className="meta-grid">
                                {renderMetaRow('Iš dokumento', extended.source_document)}
                                {renderMetaRow('Duomenų šaltinis', extended.data_source)}
                                {renderMetaRow('Tikslinė reikšmė', extended.target_value)}
                            </div>
                        )}
                        {extended.target_values && extended.target_values.length > 0 && (
                            <div className="meta-row target-values-row">
                                <span className="meta-label">Tikslinės reikšmės:</span>
                                <ul className="target-values-list">
                                    {extended.target_values.map((target, idx) => (
                                        <li key={target.id || `${target.period}-${idx}`}>
                                            <span className="meta-value">
                                                {target.period ? `${target.period} m.m. : ` : ''}
                                                {target.target_value}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {indicator.is_computed && extended.formula && (
                            <div className="meta-row formula-row">
                                <span className="meta-label">Formulė: </span>
                                <code className="meta-value">{extended.formula}</code>
                            </div>
                        )}
                        {indicator.depends_on && indicator.depends_on.length > 0 && (
                            <div className="meta-row">
                                <span className="meta-label">Priklauso nuo: </span>
                                <Space size={[4, 4]} wrap className="depends-tags">
                                    {indicator.depends_on.map((depId) => (
                                        <Tag key={depId} color="geekblue">
                                            R{depId}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </div>

                </Card>
            </Col>
        );
    };

    const handleClose = () => setIsModalOpen(false);
    const handleOpen = () => {
        setIsModalOpen(true);
        setPage(1);
    };

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchValue), 500);
        return () => clearTimeout(t);
    }, [searchValue]);

    return (
        <>
            <Button
                type="default"
                className="missing-indicators-button"
                icon={<QuestionCircleOutlined />}
                onClick={handleOpen}>
                Nerandate rodiklių?
            </Button>

            <Modal
                open={isModalOpen}
                onCancel={handleClose}
                title="Nerandate rodiklių?"
                footer={null}
                width={960}
                className="missing-indicators-modal"
                destroyOnClose>
                <Input
                    placeholder="Ieškokite rodiklių..."
                    prefix={<SearchOutlined />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    allowClear
                    className="missing-indicators-search" />

                <div className="missing-indicators-results">
                    {isFetching && <div className="loading-state"><Spin spinning={isFetching} /></div>}
                    {!isFetching && total === 0 && <Empty description="Rodiklių nerasta" />}
                    {!isFetching && total > 0 && (
                        <>
                            <Row gutter={[16, 16]}>
                                {paginatedIndicators.map(renderIndicatorCard)}
                            </Row>
                            {total > pageSize && (
                                <div className="pagination-wrapper">
                                    <Pagination
                                        current={page}
                                        pageSize={pageSize}
                                        total={total}
                                        onChange={(p, size) => {
                                            setPage(p);
                                            setPageSize(size || 20);
                                        }}
                                        showSizeChanger
                                        pageSizeOptions={['20', '30']}
                                        showTotal={(t) => `Rasta ${t} rodiklių`} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default MissingIndicatorsButton;
